import { BaseSopOperation } from "./_Base";
import { TypedNodePathParamValue } from "../../../core/Walker";
import { NodeContext } from "../../../engine/poly/NodeContext";
import { CoreMaterial } from "../../../core/geometry/Material";
import { GlobalsGeometryHandler } from "../../../engine/nodes/gl/code/globals/Geometry";
import { InputCloneMode } from "../../../engine/poly/InputCloneMode";
import { ShaderMaterial } from "three";
import { CoreObject } from "../../../core/geometry/Object";
import { isBooleanTrue } from "../../../core/BooleanValue";
export class MaterialSopOperation extends BaseSopOperation {
  constructor() {
    super(...arguments);
    this._globalsHandler = new GlobalsGeometryHandler();
    this._old_mat_by_old_new_id = /* @__PURE__ */ new Map();
    this._materials_by_uuid = /* @__PURE__ */ new Map();
  }
  static type() {
    return "material";
  }
  async cook(inputCoreGroups, params) {
    const coreGroup = inputCoreGroups[0];
    this._old_mat_by_old_new_id.clear();
    await this._applyMaterials(coreGroup, params);
    this._swapTextures(coreGroup, params);
    return coreGroup;
  }
  async _applyMaterials(coreGroup, params) {
    var _a, _b, _c, _d;
    if (!isBooleanTrue(params.assignMat)) {
      return;
    }
    const materialNode = params.material.nodeWithContext(NodeContext.MAT, (_a = this.states) == null ? void 0 : _a.error);
    if (materialNode) {
      const material = materialNode.material;
      const baseBuilderMatNode = materialNode;
      if (baseBuilderMatNode.assemblerController) {
        (_b = baseBuilderMatNode.assemblerController()) == null ? void 0 : _b.setAssemblerGlobalsHandler(this._globalsHandler);
      }
      await materialNode.compute();
      if (material) {
        const coreObjects = coreGroup.coreObjectsFromGroup(params.group);
        for (let coreObject of coreObjects) {
          this._applyMaterial(coreObject, material, params);
        }
        if (isBooleanTrue(params.applyToChildren)) {
          let _traverseCoreObject = function(coreObject, callback, level = 0) {
            if (params.group) {
              if (CoreObject.isInGroup(params.group, coreObject)) {
                callback(coreObject);
              }
            }
            const childCoreObjects = coreObject.object().children.map((child, i) => new CoreObject(child, i));
            for (let childCoreObject of childCoreObjects) {
              _traverseCoreObject(childCoreObject, callback, level + 1);
            }
          };
          for (let coreObject of coreGroup.coreObjects()) {
            _traverseCoreObject(coreObject, (childCoreObject) => {
              this._applyMaterial(childCoreObject, material, params);
            });
          }
        }
        return coreGroup;
      } else {
        (_c = this.states) == null ? void 0 : _c.error.set(`material invalid. (error: '${materialNode.states.error.message()}')`);
      }
    } else {
      (_d = this.states) == null ? void 0 : _d.error.set(`no material node found`);
    }
  }
  _swapTextures(core_group, params) {
    if (!isBooleanTrue(params.swapCurrentTex)) {
      return;
    }
    this._materials_by_uuid.clear();
    for (let object of core_group.objectsFromGroup(params.group)) {
      if (params.applyToChildren) {
        object.traverse((child) => {
          const mat = object.material;
          this._materials_by_uuid.set(mat.uuid, mat);
        });
      } else {
        const mat = object.material;
        this._materials_by_uuid.set(mat.uuid, mat);
      }
    }
    this._materials_by_uuid.forEach((mat, mat_uuid) => {
      this._swap_texture(mat, params);
    });
  }
  _applyMaterial(coreObject, src_material, params) {
    const used_material = isBooleanTrue(params.cloneMat) ? CoreMaterial.clone(this.scene(), src_material, { shareCustomUniforms: params.shareCustomUniforms }) : src_material;
    if (src_material instanceof ShaderMaterial && used_material instanceof ShaderMaterial) {
      for (let uniform_name in src_material.uniforms) {
        used_material.uniforms[uniform_name] = src_material.uniforms[uniform_name];
      }
    }
    const object = coreObject.object();
    const object_with_material = object;
    this._old_mat_by_old_new_id.set(used_material.uuid, object_with_material.material);
    object_with_material.material = used_material;
    CoreMaterial.apply_render_hook(object, used_material);
    CoreMaterial.applyCustomMaterials(object, used_material);
  }
  _swap_texture(target_mat, params) {
    if (params.texSrc0 == "" || params.texDest0 == "") {
      return;
    }
    let src_mat = this._old_mat_by_old_new_id.get(target_mat.uuid);
    src_mat = src_mat || target_mat;
    const src_tex = src_mat[params.texSrc0];
    if (src_tex) {
      target_mat[params.texDest0] = src_tex;
      const uniforms = target_mat.uniforms;
      if (uniforms) {
        const uniforms_map = uniforms[params.texDest0];
        if (uniforms_map) {
          uniforms[params.texDest0] = { value: src_tex };
        }
      }
    }
  }
}
MaterialSopOperation.DEFAULT_PARAMS = {
  group: "",
  assignMat: true,
  material: new TypedNodePathParamValue(""),
  applyToChildren: true,
  cloneMat: false,
  shareCustomUniforms: true,
  swapCurrentTex: false,
  texSrc0: "emissiveMap",
  texDest0: "map"
};
MaterialSopOperation.INPUT_CLONED_STATE = InputCloneMode.FROM_NODE;
